// Template Generator & Validator for CNC PM Checklists per ORIGINAL_REQUEST § R2 & PROJECT.md
import type { ChecklistTemplate, ChecklistItem } from '../../types';
import type { ParsedWorkbookResult } from './excelParser';

export interface TemplateMetadataInput {
  id?: string;
  name: string;
  machineModel: string;
  version?: string;
  isDefault?: boolean;
}

/**
 * Validate a ChecklistItem against interface contracts
 */
export function validateChecklistItem(item: ChecklistItem): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  if (!item || typeof item !== 'object') {
    return { valid: false, errors: ['ChecklistItem must be a non-null object'] };
  }
  if (!item.id || typeof item.id !== 'string') errors.push('Item id must be a non-empty string');
  if (typeof item.itemNo !== 'number' || item.itemNo < 1) errors.push('Item itemNo must be a positive integer');
  if (!['DAILY', 'WEEKLY', 'MONTHLY', 'HOURS'].includes(item.frequency)) {
    errors.push(`Item frequency must be 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'HOURS', got '${item.frequency}'`);
  }
  if (!item.taskNameTh || typeof item.taskNameTh !== 'string' || item.taskNameTh.trim() === '') {
    errors.push('Item taskNameTh must be a non-empty string');
  }
  if (!item.criteriaTh || typeof item.criteriaTh !== 'string') {
    errors.push('Item criteriaTh must be a non-empty string');
  }
  if (!item.methodTh || typeof item.methodTh !== 'string') {
    errors.push('Item methodTh must be a non-empty string');
  }
  if (!['done_checkbox', 'select_option', 'numeric_gauge', 'text'].includes(item.inputType)) {
    errors.push(`Item inputType must be 'done_checkbox' | 'select_option' | 'numeric_gauge' | 'text', got '${item.inputType}'`);
  }
  if (item.frequency === 'HOURS' && (typeof item.hoursThreshold !== 'number' || item.hoursThreshold <= 0)) {
    errors.push('Item with frequency HOURS must have a positive hoursThreshold');
  }
  if (item.inputType === 'numeric_gauge') {
    if (!item.unit || typeof item.unit !== 'string' || item.unit.trim() === '') {
      errors.push('Numeric gauge item must have a valid unit');
    }
  }
  if (item.inputType === 'select_option') {
    if (!Array.isArray(item.options) || item.options.length < 2) {
      errors.push('Select option item must have an options array with at least 2 choices');
    }
  }
  return { valid: errors.length === 0, errors };
}

/**
 * Validate a ChecklistTemplate against interface contracts
 */
export function validateChecklistTemplate(template: ChecklistTemplate): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  if (!template || typeof template !== 'object') {
    return { valid: false, errors: ['Template must be a non-null object'] };
  }
  if (!template.id || typeof template.id !== 'string') errors.push('Template id must be a non-empty string');
  if (!template.name || typeof template.name !== 'string') errors.push('Template name must be a non-empty string');
  if (!template.machineModel || typeof template.machineModel !== 'string') {
    errors.push('Template machineModel must be a non-empty string');
  }
  if (!template.version || typeof template.version !== 'string') {
    errors.push('Template version must be a non-empty string');
  }
  if (!Array.isArray(template.items) || template.items.length === 0) {
    errors.push('Template items must be a non-empty array');
  } else {
    template.items.forEach((item, idx) => {
      const res = validateChecklistItem(item);
      if (!res.valid) {
        errors.push(`Item[${idx}] "${item.id || idx}": ${res.errors.join(', ')}`);
      }
    });
  }
  return { valid: errors.length === 0, errors };
}

/**
 * Generate a complete, compliant ChecklistTemplate from ParsedWorkbookResult
 */
export function generateTemplateFromParsedWorkbook(
  parsed: ParsedWorkbookResult,
  meta: TemplateMetadataInput
): ChecklistTemplate {
  const templateId =
    meta.id && meta.id.trim()
      ? meta.id.trim()
      : `tpl_${meta.machineModel.toLowerCase().replace(/[^a-z0-9]/g, '_')}_${Date.now()}`;

  const now = new Date().toISOString();

  // Normalize items ensuring all interface contracts are satisfied
  const normalizedItems: ChecklistItem[] = parsed.allItems.map((item, index) => {
    const itemNo = item.itemNo && item.itemNo > 0 ? item.itemNo : index + 1;
    const frequency = item.frequency || 'DAILY';
    const cleanId = item.id || `${frequency.toLowerCase()}_${itemNo}`;

    const normalized: ChecklistItem = {
      id: cleanId,
      itemNo,
      frequency,
      taskNameTh: item.taskNameTh || `รายการตรวจเช็กที่ ${itemNo}`,
      criteriaTh: item.criteriaTh || 'ปกติ ผ่านเกณฑ์',
      methodTh: item.methodTh || 'สายตา (Visual)',
      inputType: item.inputType || 'select_option',
    };

    if (item.taskNameEn) normalized.taskNameEn = item.taskNameEn;
    if (item.criteriaEn) normalized.criteriaEn = item.criteriaEn;
    if (item.methodEn) normalized.methodEn = item.methodEn;

    if (normalized.inputType === 'numeric_gauge') {
      normalized.unit = item.unit || 'MPa';
      if (item.minValue !== undefined) normalized.minValue = item.minValue;
      if (item.maxValue !== undefined) normalized.maxValue = item.maxValue;
      if (item.criticalLimit) normalized.criticalLimit = item.criticalLimit;
    } else if (normalized.inputType === 'select_option') {
      normalized.options =
        Array.isArray(item.options) && item.options.length >= 2
          ? item.options
          : ['ปกติ', 'ชำรุด'];
    }

    if (frequency === 'HOURS') {
      normalized.hoursThreshold =
        item.hoursThreshold && item.hoursThreshold > 0 ? item.hoursThreshold : 500;
    }

    return normalized;
  });

  const template: ChecklistTemplate = {
    id: templateId,
    name: meta.name.trim(),
    machineModel: meta.machineModel.trim(),
    version: meta.version ? String(meta.version).trim() : '1.0.0',
    isDefault: Boolean(meta.isDefault),
    items: normalizedItems,
    lubricants: parsed.lubricants.length > 0 ? parsed.lubricants : undefined,
    safetyCautions: parsed.safetyCautions.length > 0 ? parsed.safetyCautions : undefined,
    createdAt: now,
    updatedAt: now,
  };

  const validation = validateChecklistTemplate(template);
  if (!validation.valid) {
    throw new Error(`Generated template failed validation: ${validation.errors.join('; ')}`);
  }

  return template;
}

/**
 * Export ChecklistTemplate as formatted JSON string
 */
export function exportTemplateToJson(template: ChecklistTemplate): string {
  return JSON.stringify(template, null, 2);
}

/**
 * Parse and validate ChecklistTemplate from JSON string
 */
export function importTemplateFromJson(jsonStr: string): ChecklistTemplate {
  const parsed = JSON.parse(jsonStr);
  const validation = validateChecklistTemplate(parsed);
  if (!validation.valid) {
    throw new Error(`Invalid template JSON: ${validation.errors.join('; ')}`);
  }
  return parsed as ChecklistTemplate;
}
